import { useState, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { AMBER, BORDER_COLOR } from '../../styles/tokens';

// V31: multi-select for accounting Nómina employee filter.
// Mirrors ProjectFilter but works on objects {id, name, external_id} where
// `selected` is an array of numeric ids.
export default function EmployeeFilter({ employees, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function toggle(id) {
    const next = selected.includes(id)
      ? selected.filter(x => x !== id)
      : [...selected, id];
    onChange(next);
  }

  function clearAll(e) {
    e.stopPropagation();
    onChange([]);
  }

  const hasSelection = selected.length > 0;
  const label = hasSelection
    ? `${selected.length} empleado${selected.length !== 1 ? 's' : ''}`
    : 'Todos los empleados';

  const q = search.trim().toLowerCase();
  const filtered = q
    ? employees.filter(e =>
        (e.name || '').toLowerCase().includes(q) ||
        (e.external_id || '').toLowerCase().includes(q)
      )
    : employees;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(prev => !prev)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '6px 12px', fontSize: 13, fontWeight: hasSelection ? 600 : 500,
          borderRadius: 20, cursor: 'pointer', fontFamily: 'Inter, sans-serif',
          border: hasSelection ? `1.5px solid ${AMBER}` : `1px solid ${BORDER_COLOR}`,
          background: hasSelection ? 'rgba(217,119,6,0.08)' : '#fff',
          color: hasSelection ? AMBER : '#475569',
          whiteSpace: 'nowrap',
          height: 34, minWidth: 180,
        }}
      >
        <span>{label}</span>
        {hasSelection ? (
          <X size={13} onClick={clearAll} style={{ cursor: 'pointer' }} />
        ) : (
          <ChevronDown size={13} />
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, marginTop: 4,
          background: '#fff', border: `1px solid ${BORDER_COLOR}`,
          borderRadius: 10, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 50, minWidth: 260, maxHeight: 320, overflowY: 'auto',
          padding: '6px 0',
        }}>
          <div style={{ padding: '6px 10px', borderBottom: `1px solid ${BORDER_COLOR}` }}>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar…"
              style={{
                width: '100%', padding: '5px 8px', fontSize: 12,
                border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                fontFamily: 'Inter, sans-serif',
              }}
            />
          </div>
          {filtered.length === 0 ? (
            <div style={{ padding: '10px 14px', fontSize: 12, color: '#94a3b8' }}>
              {employees.length === 0 ? 'Sin empleados' : 'Sin coincidencias'}
            </div>
          ) : (
            filtered.map(emp => {
              const isSelected = selected.includes(emp.id);
              return (
                <label
                  key={emp.id}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 14px', cursor: 'pointer', fontSize: 13,
                    background: isSelected ? 'rgba(217,119,6,0.06)' : 'transparent',
                    color: '#1e293b', fontWeight: isSelected ? 600 : 400,
                  }}
                  onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f8fafc'; }}
                  onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(emp.id)}
                    style={{ accentColor: AMBER }}
                  />
                  <span style={{ flex: 1 }}>{emp.name}</span>
                  {emp.external_id && (
                    <span style={{ fontSize: 10, color: '#94a3b8', fontFamily: 'monospace' }}>
                      {emp.external_id}
                    </span>
                  )}
                </label>
              );
            })
          )}
          {hasSelection && (
            <div style={{ borderTop: `1px solid ${BORDER_COLOR}`, padding: '6px 14px' }}>
              <button
                onClick={(e) => { e.stopPropagation(); onChange([]); }}
                style={{
                  background: 'none', border: 'none', fontSize: 12,
                  color: '#64748b', cursor: 'pointer', fontFamily: 'Inter, sans-serif',
                  textDecoration: 'underline', padding: 0,
                }}
              >
                Limpiar filtro
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
